/**
 * Secure Error Handling Service
 * Filters PII and sensitive information from error messages and logs
 */

/**
 * PII patterns to detect and redact
 */
const PII_PATTERNS = {
  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,

  // Phone numbers (various formats)
  phone: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,

  // Social Security Numbers
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,

  // Credit card numbers
  creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,

  // Dates of birth (various formats)
  dob: /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g,

  // API keys and tokens
  apiKey: /\b[Aa]pi[_-]?[Kk]ey[:\s]*['\"]?[A-Za-z0-9_-]{16,}['\"]?\b/g,
  token: /\b[Tt]oken[:\s]*['\"]?[A-Za-z0-9_-]{16,}['\"]?\b/g,
  bearer: /[Bb]earer\s+[A-Za-z0-9_-]+/g,

  // Passwords
  password: /[Pp]assword[:\s]*['\"]?[^\s'"]{4,}['\"]?\b/g,

  // IP addresses
  ipAddress: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
};

/**
 * Sensitive field names to redact
 */
const SENSITIVE_FIELDS = new Set([
  'password',
  'ssn',
  'social_security_number',
  'credit_card',
  'card_number',
  'cvv',
  'pin',
  'api_key',
  'token',
  'secret',
  'private_key',
  'access_token',
  'refresh_token',
]);

/**
 * User-friendly error messages
 */
const USER_ERROR_MESSAGES: Record<string, string> = {
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT: 'The request took too long to complete. Please try again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access to this resource is forbidden.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'A server error occurred. Please try again later.',
  RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Secure error object
 */
export interface SecureError {
  /** User-safe error message */
  message: string;

  /** Error code */
  code: string;

  /** Error severity */
  severity: ErrorSeverity;

  /** Request ID for tracking */
  requestId?: string;

  /** Timestamp */
  timestamp: string;

  /** Additional context (sanitized) */
  context?: Record<string, unknown>;
}

/**
 * Secure Error Handling Service
 */
export class ErrorHandlingService {
  /**
   * Sanitize error message by removing PII
   */
  public sanitizeMessage(message: string): string {
    let sanitized = message;

    // Replace all PII patterns with redacted text
    Object.entries(PII_PATTERNS).forEach(([type, pattern]) => {
      sanitized = sanitized.replace(pattern, `[REDACTED_${type.toUpperCase()}]`);
    });

    return sanitized;
  }

  /**
   * Sanitize error object by removing PII and sensitive fields
   */
  public sanitizeError(error: Error | any): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {
      name: error.name || 'Error',
      message: this.sanitizeMessage(error.message || 'Unknown error'),
    };

    // Sanitize additional properties
    if (error.stack && import.meta.env.DEV) {
      // Only include stack trace in development
      sanitized.stack = this.sanitizeMessage(error.stack);
    }

    // Sanitize custom properties
    Object.keys(error).forEach((key) => {
      if (key !== 'name' && key !== 'message' && key !== 'stack') {
        const value = error[key];

        if (this.isSensitiveField(key)) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'string') {
          sanitized[key] = this.sanitizeMessage(value);
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizeObject(value);
        } else {
          sanitized[key] = value;
        }
      }
    });

    return sanitized;
  }

  /**
   * Sanitize object recursively
   */
  private sanitizeObject(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) =>
        typeof item === 'object' ? this.sanitizeObject(item) : this.sanitizeValue(item)
      );
    }

    const sanitized: Record<string, unknown> = {};

    Object.entries(obj).forEach(([key, value]) => {
      if (this.isSensitiveField(key)) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'string') {
        sanitized[key] = this.sanitizeMessage(value);
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  /**
   * Sanitize a single value
   */
  private sanitizeValue(value: any): any {
    if (typeof value === 'string') {
      return this.sanitizeMessage(value);
    }
    return value;
  }

  /**
   * Check if field name is sensitive
   */
  private isSensitiveField(fieldName: string): boolean {
    const lowerField = fieldName.toLowerCase();
    return Array.from(SENSITIVE_FIELDS).some((sensitive) => lowerField.includes(sensitive));
  }

  /**
   * Create secure error for user display
   */
  public createSecureError(
    error: Error | any,
    code?: string,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM
  ): SecureError {
    // Get user-friendly message
    const errorCode = code || error.code || 'UNKNOWN_ERROR';
    const message = USER_ERROR_MESSAGES[errorCode] || USER_ERROR_MESSAGES.UNKNOWN_ERROR;

    // Create secure error object
    const secureError: SecureError = {
      message,
      code: errorCode,
      severity,
      timestamp: new Date().toISOString(),
    };

    // Add sanitized context in development
    if (import.meta.env.DEV) {
      secureError.context = this.sanitizeError(error);
    }

    return secureError;
  }

  /**
   * Log error securely
   */
  public logError(error: Error | any, context?: Record<string, unknown>): void {
    const sanitizedError = this.sanitizeError(error);
    const sanitizedContext = context ? this.sanitizeObject(context) : {};

    if (import.meta.env.DEV) {
      console.error('[Error]', sanitizedError, sanitizedContext);
    }

    // In production, send to error tracking service
    if (import.meta.env.PROD) {
      this.sendToErrorTracking(sanitizedError, sanitizedContext);
    }
  }

  /**
   * Send error to tracking service (e.g., Sentry)
   */
  private sendToErrorTracking(
    error: Record<string, unknown>,
    context: Record<string, unknown>
  ): void {
    // TODO: Integrate with error tracking service
    // Example: Sentry.captureException(error, { extra: context });

    // For now, just log to console in production
    console.error('[Error Tracking]', error, context);
  }

  /**
   * Determine error severity
   */
  public determineSeverity(error: Error | any): ErrorSeverity {
    const errorCode = error.code || error.statusCode;

    // Critical errors
    if (errorCode === 500 || errorCode === 503) {
      return ErrorSeverity.CRITICAL;
    }

    // High severity errors
    if (errorCode === 401 || errorCode === 403 || errorCode === 404) {
      return ErrorSeverity.HIGH;
    }

    // Medium severity errors
    if (errorCode === 400 || errorCode === 422) {
      return ErrorSeverity.MEDIUM;
    }

    // Low severity errors
    return ErrorSeverity.LOW;
  }

  /**
   * Check if error contains PII
   */
  public containsPII(text: string): boolean {
    return Object.values(PII_PATTERNS).some((pattern) => pattern.test(text));
  }

  /**
   * Global error handler setup
   */
  public setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      event.preventDefault();
      this.logError(event.reason, {
        type: 'unhandledRejection',
        promise: 'Promise',
      });
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      event.preventDefault();
      this.logError(event.error || event.message, {
        type: 'globalError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    console.log('[ErrorHandling] Global error handlers setup');
  }
}

/**
 * Singleton error handling service instance
 */
let errorHandlingServiceInstance: ErrorHandlingService | null = null;

/**
 * Get or create error handling service instance
 */
export function getErrorHandlingService(): ErrorHandlingService {
  if (!errorHandlingServiceInstance) {
    errorHandlingServiceInstance = new ErrorHandlingService();
    errorHandlingServiceInstance.setupGlobalErrorHandlers();
  }
  return errorHandlingServiceInstance;
}

/**
 * Convenience functions
 */
export const secureError = {
  sanitize: (error: Error | any) => getErrorHandlingService().sanitizeError(error),
  create: (error: Error | any, code?: string, severity?: ErrorSeverity) =>
    getErrorHandlingService().createSecureError(error, code, severity),
  log: (error: Error | any, context?: Record<string, unknown>) =>
    getErrorHandlingService().logError(error, context),
};
